import {CreateAccessor} from "mobx-graphlink";
import {useThree} from "@react-three/fiber";
import React, {useMemo} from "react";
import * as THREE from "three";
import {MapState} from "../../../../Store/main/maps/@MapState.js";
import {IsMapObjectHidden, ResolveObjPosInWorld, ResolveObjRotInWorld} from "../../../../Server/DB/mapObjects/$mapObject.js";
import {MapObject} from "../../../../Server/DB/mapObjects/@MapObject.js";
import {DMap} from "../../../../Server/DB/maps/@Map.js";
import {observer_mgl_r3} from "../../../../Utils/ThreeJS/General.js";
import {Vec2} from "../../../../Utils/ThreeJS/VectorStructs.js";
import {mapZIndexes} from "./MapZIndexes.js";

/** Ground reach (metres) of the view axis — where the pitched-down line of sight hits the ground.
 *  eyeHeight / tan(pitch). Guarded so a 0 pitch (looking at the horizon) doesn't produce an
 *  infinite reach (clamped to a large-but-finite default).  Uses the BAKED absolute `eyeHeight`
 *  (the actual camera height above world z=0) — matches the 3D camera's geometry. */
function ResolveViewpointReach(eyeHeight: number, pitchDeg: number): number {
	const pitchRad = pitchDeg * Math.PI / 180;
	const tanPitch = Math.tan(pitchRad);
	if (tanPitch <= 1e-6) return 100000; // looking at the horizon — cap the wedge so it stays visible
	return eyeHeight / tanPitch;
}

/** Convert the viewpoint's VERTICAL fov to the HORIZONTAL half-angle, given the viewport aspect.
 *  three.js `fov` is always the VERTICAL fov; the horizontal half-angle is `atan(tan(vfov/2)·aspect)`.
 *  (Matches `resolvePerspectiveViewportBounds` + `buildPerspectiveCamera`, which treat `fov` as
 *  vertical.)  A wide viewport therefore shows a much wider horizontal wedge than the raw vertical
 *  fov implies — ignoring the aspect was drawing the wedge ~2–4× too narrow. */
function ResolveViewpointHFovHalfAngle(fovDeg: number, aspect: number): number {
	const vfovHalfTan = Math.tan((fovDeg * Math.PI / 180) / 2);
	return Math.atan(vfovHalfTan * Math.max(1e-3, aspect));
}

/** Ground-projected frustum footprint for a viewpoint, expressed as a quadrilateral
 *  (near-edge at the eye, far-edge at the ground reach, spreading by the horizontal fov).
 *  `aspect` = viewport width/height (px); needed to convert the modifier's VERTICAL fov to the
 *  horizontal spread the user actually sees (matches the 3D camera's frustum).
 *  `maxProjectionDistance` (optional): clamp the ground reach to this many metres from the eye. */
export const GetViewpointWedgePoints_World = CreateAccessor((obj: MapObject, aspect: number, maxProjectionDistance: number|n = null): Vec2[]|n=>{
	const vp = obj.modifiers?.map(m=>m.viewpoint).filter(a=>a != null)[0];
	if (!vp) return null;
	const objPos = ResolveObjPosInWorld(obj);
	if (!objPos) return null;

	const eyePos = new Vec2(objPos.x, objPos.y);
	// The modifier's yaw is local to the container; add the container's world rotation (itself +
	// ancestors, same clockwise-from-+y convention) so a rotated container aims the wedge with it.
	const yaw = vp.yaw + (ResolveObjRotInWorld(obj) ?? 0);
	const yawRad = yaw * Math.PI / 180;
	let reach = ResolveViewpointReach(vp.eyeHeight, vp.pitch);
	// Clamp the reach to the projection max-distance when set (>0).  A 0-pitch camera (pitch=0 →
	// `ResolveViewpointReach` already caps at 100000 m) would otherwise draw a wedge extending to
	// near-infinity; this lets the user bound it explicitly.  No clamp when unset/null/≤0.
	if (maxProjectionDistance != null && maxProjectionDistance > 0) reach = Math.min(reach, maxProjectionDistance);
	const hfovHalfAngle = ResolveViewpointHFovHalfAngle(vp.fov, aspect);
	const hw = reach * Math.tan(hfovHalfAngle);

	// yaw measured from map +y (forward/north); yaw=0 => looking toward +y.
	const forward = new Vec2(Math.sin(yawRad), Math.cos(yawRad));
	const right = new Vec2(Math.cos(yawRad), -Math.sin(yawRad));

	const baseCenter = eyePos.Plus(forward.Times(reach));
	const baseL = baseCenter.Minus(right.Times(hw));
	const baseR = baseCenter.Plus(right.Times(hw));

	return [eyePos, baseL, baseR];
});

/** The detailed-solve centre (metres) for a viewpoint, or null when `detailedSolveCenter_distance`
 *  is unset/≤0 (no offset — the solver centres on the eye).  `eye + yawDir × distance`, matching the
 *  renderer's solve-centre computation (TerrainRenderer).  `null` so the overlay can hide the marker. */
export const GetViewpointSolveCenter_World = CreateAccessor((obj: MapObject): Vec2|null=>{
	const vp = obj.modifiers?.map(m=>m.viewpoint).filter(a=>a != null)[0];
	if (!vp) return null;
	const distance = vp.detailedSolveCenter_distance;
	if (distance == null || !Number.isFinite(distance) || distance <= 0) return null;
	const objPos = ResolveObjPosInWorld(obj);
	if (!objPos) return null;

	const eyePos = new Vec2(objPos.x, objPos.y);
	// Same local→world yaw correction as the wedge points (container world rotation + local yaw).
	const yaw = vp.yaw + (ResolveObjRotInWorld(obj) ?? 0);
	const yawRad = yaw * Math.PI / 180;
	// yaw measured from map +y (forward/north); yaw=0 => looking toward +y.
	const forward = new Vec2(Math.sin(yawRad), Math.cos(yawRad));
	return eyePos.Plus(forward.Times(distance));
});

/** Set15 P0: draw a ground-projected frustum wedge for every `Mod_Viewpoint`, to aim viewpoints in 2D.
 *  Pure 2D overlay (no render change); hidden in screenshot mode, like the other markers. */
export const ViewpointOverlay = observer_mgl_r3((props: {map: DMap, mapState: MapState, objects: MapObject[]})=>{
	const {mapState, objects} = props;
	// The viewport's pixel aspect — needed to convert the viewpoint's VERTICAL fov (three.js
	// convention) to the HORIZONTAL wedge spread the user actually sees (matches the 3D camera's
	// frustum, which derives horizontal fov from `atan(tan(vfov/2)·aspect)`).  Read at the top so
	// the hook is always called in the same order (early returns below don't reorder it).
	const {size} = useThree();
	const aspect = size.height > 0 ? size.width / size.height : 1;
	if (!mapState.settings.general.showViewpoints) return null;
	if (mapState.settings.general.screenshotMode) return null;
	const maxProjectionDistance = mapState.settings.general.viewpoints_maxProjectionDistance;

	const wedges = objects
		.filter(obj=>!IsMapObjectHidden(obj, objects))
		.map(obj=>({
			obj,
			points: GetViewpointWedgePoints_World(obj, aspect, maxProjectionDistance),
			solveCenter: GetViewpointSolveCenter_World(obj),
		}))
		.filter(a=>a.points != null);

	if (wedges.length == 0) return null;
	return (
		<>
			{wedges.map(({obj, points, solveCenter})=>(
				<ViewpointWedge key={obj.id} points={points!} solveCenter={solveCenter}/>
			))}
		</>
	);
});

const ViewpointWedge = observer_mgl_r3((props: {points: Vec2[], solveCenter: Vec2|null})=>{
	const {points, solveCenter} = props;

	const {fillGeometry, outlinePositions, solveCenterPositions} = useMemo(()=>{
		// fill: closed polygon mesh (mirrors PathFillUI's CreateComplexPolygonMesh)
		const shape = new THREE.Shape();
		shape.moveTo(points[0].x, points[0].y);
		for (let i = 1; i < points.length; i++) shape.lineTo(points[i].x, points[i].y);
		shape.closePath();
		const fillGeometry = new THREE.ShapeGeometry(shape);

		// outline: draw the two frustum edges FROM the apex (the eye), leaving the far base edge open
		// so the wedge reads as opening forward in the view direction (the V-corner is at the apex).
		const [A, L, R] = points;
		const outlinePositions = new Float32Array([
			A.x, A.y, 0, L.x, L.y, 0,
			A.x, A.y, 0, R.x, R.y, 0,
		]);

		// Solve-centre marker: a small crosshair (two perpendicular line segments) so the user can
		// see exactly where the detailed-solve region is centred.  Rendered only when the viewpoint
		// sets `detailedSolveCenter_distance > 0`.
		let solveCenterPositions: Float32Array|null = null;
		if (solveCenter) {
			const s = 1; // half-size of the crosshair, metres
			solveCenterPositions = new Float32Array([
				solveCenter.x - s, solveCenter.y, 0, solveCenter.x + s, solveCenter.y, 0,
				solveCenter.x, solveCenter.y - s, 0, solveCenter.x, solveCenter.y + s, 0,
			]);
		}

		return {fillGeometry, outlinePositions, solveCenterPositions};
	}, [points, solveCenter]);

	return (
		<>
			{/* Points are absolute world coords, so the mesh sits at the origin with only z to the overlay layer. */}
			<mesh position={new THREE.Vector3(0, 0, mapZIndexes.viewpointOverlay)}>
				<primitive attach="geometry" object={fillGeometry}/>
				<meshBasicMaterial attach="material" color="#ffd54f" transparent={true} opacity={0.18}
					side={THREE.DoubleSide} depthTest={false} depthWrite={false}/>
				<line>
					<bufferGeometry>
						<bufferAttribute attach="attributes-position" args={[outlinePositions, 3]}/>
					</bufferGeometry>
					<lineBasicMaterial color="#ffd54f" transparent={true} opacity={0.9} depthTest={false} depthWrite={false}/>
				</line>
				{solveCenterPositions && (
					// Use LineSegments (NOT Line): the crosshair is two independent segments (horizontal
					// + vertical); a plain `Line` would connect all 4 vertices into one continuous polyline,
					// wrongly joining the right end to the bottom end.
					<lineSegments>
						<bufferGeometry>
							<bufferAttribute attach="attributes-position" args={[solveCenterPositions, 3]}/>
						</bufferGeometry>
						<lineBasicMaterial color="#00e5ff" transparent={true} opacity={0.95} depthTest={false} depthWrite={false}/>
					</lineSegments>
				)}
			</mesh>
		</>
	);
});
